import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Sparkles,
  ArrowLeft,
  Building2,
  Tag,
  Lightbulb,
  ExternalLink,
  GitBranch,
  Video,
  FileText,
  Link2,
  Calendar,
} from 'lucide-react';
import { showcaseService, ShowcaseEntry } from '../services/showcase';

const ASSET_ICONS: Record<string, React.ElementType> = {
  repo: GitBranch,
  video: Video,
  doc: FileText,
  other: Link2,
};

export default function ShowcaseDetail() {
  const { showcaseId } = useParams<{ showcaseId: string }>();
  const navigate = useNavigate();
  const [entry, setEntry] = useState<ShowcaseEntry | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!showcaseId) return;
    showcaseService.get(showcaseId)
      .then(setEntry)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [showcaseId]);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center text-slate-400 text-sm font-display">
        Loading…
      </div>
    );
  }

  if (!entry) {
    return (
      <div className="flex-1 flex items-center justify-center text-slate-400 text-sm font-display">
        Showcase entry not found.
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto p-8 font-display">
      <div className="w-full max-w-[900px] mx-auto flex flex-col gap-6">

        {/* Back + header */}
        <div>
          <button
            onClick={() => navigate('/showcase')}
            className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-navy transition-colors mb-4"
          >
            <ArrowLeft size={14} /> Back to Showcase
          </button>
          <div className="flex items-start gap-4">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary flex-shrink-0 mt-1">
              <Sparkles size={22} />
            </div>
            <div className="flex-1">
              <h1 className="text-2xl font-bold tracking-tight text-navy leading-snug">
                {entry.challengeTitle || 'Untitled challenge'}
              </h1>
              <div className="flex items-center gap-3 mt-2 flex-wrap">
                {entry.industry && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20 uppercase tracking-wider">
                    <Building2 size={10} /> {entry.industry}
                  </span>
                )}
                {entry.publishedAt && (
                  <span className="text-xs text-slate-400 flex items-center gap-1">
                    <Calendar size={11} />
                    {new Date(entry.publishedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Summary */}
        {entry.challengeSummary && (
          <div className="bg-white border border-[#e2e8f0] rounded-xl p-6">
            <h2 className="text-[11px] font-bold text-primary tracking-widest uppercase mb-3">Challenge Summary</h2>
            <p className="text-sm text-slate-600 leading-relaxed">{entry.challengeSummary}</p>
          </div>
        )}

        {/* Tags */}
        {entry.tags && entry.tags.length > 0 && (
          <div className="bg-white border border-[#e2e8f0] rounded-xl p-6">
            <h2 className="text-[11px] font-bold text-primary tracking-widest uppercase mb-3 flex items-center gap-1.5">
              <Tag size={11} /> Topics &amp; Methods
            </h2>
            <div className="flex flex-wrap gap-2">
              {entry.tags.map(tag => (
                <span key={tag} className="text-xs font-medium px-3 py-1 rounded-full bg-slate-100 text-slate-600 border border-slate-200">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Learnings */}
        {entry.learnings && entry.learnings.length > 0 && (
          <div className="bg-white border border-[#e2e8f0] rounded-xl p-6">
            <h2 className="text-[11px] font-bold text-primary tracking-widest uppercase mb-3 flex items-center gap-1.5">
              <Lightbulb size={11} /> Key Learnings
            </h2>
            <ul className="flex flex-col gap-2">
              {entry.learnings.map((learning, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                  <span className="w-5 h-5 rounded-full bg-primary/10 text-primary flex items-center justify-center text-[10px] font-bold flex-shrink-0 mt-0.5">
                    {i + 1}
                  </span>
                  {learning}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Assets */}
        {entry.assets && entry.assets.length > 0 && (
          <div className="bg-white border border-[#e2e8f0] rounded-xl p-6">
            <h2 className="text-[11px] font-bold text-primary tracking-widest uppercase mb-3 flex items-center gap-1.5">
              <ExternalLink size={11} /> Shared Assets
            </h2>
            <div className="flex flex-col gap-2">
              {entry.assets.map((asset, i) => {
                const Icon = ASSET_ICONS[asset.type] || Link2;
                return (
                  <a
                    key={i}
                    href={asset.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-3 rounded-xl border border-[#e2e8f0] hover:border-primary/40 hover:bg-slate-50 transition-all text-sm text-navy group"
                  >
                    <div className="w-8 h-8 rounded-lg bg-slate-100 group-hover:bg-primary/10 group-hover:text-primary flex items-center justify-center transition-colors text-slate-400 flex-shrink-0">
                      <Icon size={15} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{asset.label || asset.url}</p>
                      <p className="text-xs text-slate-400 capitalize">{asset.type}</p>
                    </div>
                    <ExternalLink size={13} className="text-slate-400 group-hover:text-primary flex-shrink-0 transition-colors" />
                  </a>
                );
              })}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
